// src/pages/ProjectLoginPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  TextField,
  Button,
  Typography,
  ThemeProvider,
  IconButton,
  InputAdornment,
  useMediaQuery,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Brightness4, Brightness7, Visibility, VisibilityOff } from '@mui/icons-material';
import { setUserToLocal, setLogOut } from '../store/auth/reducer';
import { validateStoredToken } from '../store/auth/thunks';
import { clearAuthStorage, getStoredAuth, storeAuth } from '../utils/authStorage';
import appTheme from '../theme/appTheme';
import { useMobileTheme } from '../contexts/MobileThemeContext';
import PublicLocaleToggle from '../components/PublicLocaleToggle';
import { TechBackground } from '../components/TechBackground';
import { resolveProjectAdminLandingPath } from '../utils/projectAdminLanding';
import { usePublicUiLocale } from '../contexts/PublicUiLocaleContext';

const ProjectLoginPage = () => {
  const { slug: projectSlug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authState = useSelector((state) => state.authSlice || { result: false });
  const { mobileTheme, toggleMobileTheme } = useMobileTheme();
  const { lang, setLang, projectLogin: pl } = usePublicUiLocale();
  const isMobile = useMediaQuery('(max-width:600px)');

  const handleLogoutProject = useCallback(() => {
    dispatch(setLogOut());
    if (projectSlug) {
      navigate(`/p/${projectSlug}/login`, { replace: true });
    }
  }, [dispatch, navigate, projectSlug]);
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [projectInfo, setProjectInfo] = useState(null);
  const [projectExists, setProjectExists] = useState(true);
  const [checkingProject, setCheckingProject] = useState(true);
  const [checkedStoredAuth, setCheckedStoredAuth] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const redirectByRole = useCallback(async (role, token) => {
    if (role === 'superadmin') {
      navigate('/v2/superadmin', { replace: true });
      return;
    }
    if (role === 'admin') {
      const path = await resolveProjectAdminLandingPath(projectSlug, { role, token });
      navigate(path, { replace: true });
      return;
    }
    navigate(`/p/${projectSlug}/groups`, { replace: true });
  }, [navigate, projectSlug]);

  // Проверяем существование проекта при загрузке
  useEffect(() => {
    if (projectSlug) {
      setCheckingProject(true);
      setProjectInfo({ name: projectSlug });
      setProjectExists(true); // По умолчанию считаем, что проект существует
      
      // Проверяем существование проекта через открытый API endpoint
      fetch(`/api/p/${projectSlug}/check`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then(async (response) => {
          const data = await response.json().catch(() => ({}));
          
          if (response.ok && data.exists === true) {
            // Проект существует
            const displayName = (typeof data.name === 'string' && data.name.trim())
              ? data.name.trim()
              : projectSlug;
            setProjectInfo({ name: displayName });
            setProjectExists(true);
          } else {
            // Проект не найден
            setProjectExists(false);
          }
        })
        .catch((error) => {
          // При ошибке сети считаем, что проект существует (чтобы не блокировать вход)
          console.warn('ProjectLoginPage: Network error checking project:', error);
          setProjectExists(true);
        })
        .finally(() => {
          setCheckingProject(false);
        });
    } else {
      setCheckingProject(false);
      setProjectExists(false);
    }
  }, [projectSlug]);

  // Убираем старый useEffect для редиректа, так как теперь делаем это вручную после логина

  useEffect(() => {
    if (!projectSlug || checkedStoredAuth || authState.result) return;
    const storedAuth = getStoredAuth();
    const storedUser = storedAuth?.user || {};
    const storedToken = storedAuth?.token || null;
    const storedProject = String(storedUser?.projectSlug || '').trim().toLowerCase();
    const currentProject = String(projectSlug || '').trim().toLowerCase();

    setCheckedStoredAuth(true);
    if (!storedToken || !storedUser?.result || storedProject !== currentProject) {
      return;
    }

    dispatch(validateStoredToken())
      .unwrap()
      .then((resp) => {
        const role = resp?.user?.role || storedUser?.role || 'user';
        redirectByRole(role, storedToken);
      })
      .catch(() => {
        clearAuthStorage();
        setCheckedStoredAuth(false);
      });
  }, [
    projectSlug,
    checkedStoredAuth,
    authState.result,
    dispatch,
    redirectByRole,
  ]);

  useEffect(() => {
    if (!projectSlug || !authState?.result) return;
    const authProject = String(authState?.projectSlug || '').trim().toLowerCase();
    const currentProject = String(projectSlug || '').trim().toLowerCase();
    if (authProject && authProject !== currentProject) return;
    const storedAuth = getStoredAuth();
    const token = storedAuth?.token || localStorage.getItem('token') || sessionStorage.getItem('token');
    redirectByRole(authState?.role || 'user', token);
  }, [projectSlug, authState?.result, authState?.role, authState?.projectSlug, redirectByRole]);

  // loadProjectInfo удалён: информация о проекте доступна после входа

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');


    try {
      // Логинимся через простой проектный API endpoint
      const response = await fetch(`/api/p/${projectSlug}/project-auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          projectSlug: projectSlug,
          lang,
        })
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(pl.badUserPass);
        }
        const errorData = await response.json();
        throw new Error(errorData.message || pl.errorLogin);
      }
      
      const responseData = await response.json();
      
      if (responseData?.access_token) {
        // Сохраняем токен и информацию о пользователе
        const u = responseData.user || {};
        const commandPriority = Math.max(
          0,
          Math.min(
            9,
            Math.trunc(
              Number(
                u.commandPriority ?? u.commandpriority ?? u.command_priority ?? 0,
              ) || 0,
            ),
          ),
        );
        const userData = {
          id: u.id,
          result: true,
          role: u.role,
          userName: u.username,
          commandPriority,
          visibleGroups: u.visibleGroups || [],
          projectSlug: projectSlug
        };
        
        // Сохраняем в localStorage или sessionStorage в зависимости от rememberMe
        storeAuth({ user: userData, token: responseData.access_token, rememberMe: formData.rememberMe });
        
        // Обновляем Redux state
        dispatch(setUserToLocal(userData));
        
        // Добавляем небольшую задержку перед редиректом
        // чтобы JWT токен успел установиться
        setTimeout(async () => {
          const role = responseData.user.role;
          if (role === 'superadmin') {
            navigate('/v2/superadmin', { replace: true });
            return;
          }
          if (role === 'admin') {
            const path = await resolveProjectAdminLandingPath(projectSlug, {
              role,
              token: responseData.access_token,
            });
            navigate(path, { replace: true });
            return;
          }
          navigate(`/p/${projectSlug}/groups`, { replace: true });
        }, 2000);
      } else {
        setError(pl.badCredentials);
      }
    } catch (err) {
      console.error('Login error:', err);
      // Очищаем все данные авторизации при ошибке
      clearAuthStorage();
      // Устанавливаем понятное сообщение об ошибке
      const errorMessage = err?.response?.data?.message || err?.message || pl.badUserPass;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!projectSlug) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh' 
      }}>
        <Alert severity="error">{pl.noProjectSpecified}</Alert>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={appTheme}>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100vw',
          height: '100vh',
          bgcolor: mobileTheme === 'white' ? '#f5f5f5' : '#0a1a2f',
          overflow: 'hidden',
          boxSizing: 'border-box',
          px: { xs: 1, sm: 2 },
          py: { xs: 2, sm: 3 },
          pt: { xs: 9, sm: 8 },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
          }}
        >
          {authState.result && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleLogoutProject}
              sx={{
                color: mobileTheme === 'white' ? '#c62828' : '#ffcdd2',
                borderColor: mobileTheme === 'white' ? 'rgba(198,40,40,0.6)' : 'rgba(255,255,255,0.45)',
                textTransform: 'none',
                '&:hover': {
                  borderColor: mobileTheme === 'white' ? '#c62828' : '#fff',
                  bgcolor: mobileTheme === 'white' ? 'rgba(198,40,40,0.06)' : 'rgba(255,255,255,0.08)',
                },
              }}
            >
              {pl.logout}
            </Button>
          )}
          <PublicLocaleToggle
            lang={lang}
            onChange={setLang}
            labelUk={pl.languageLabelUk}
            labelEn={pl.languageLabelEn}
            tone={mobileTheme === 'white' ? 'light' : 'dark'}
          />
          {isMobile && (
            <IconButton
              onClick={toggleMobileTheme}
              sx={{
                bgcolor: mobileTheme === 'white' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                color: mobileTheme === 'white' ? '#333' : '#fff',
                '&:hover': {
                  bgcolor: mobileTheme === 'white' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              {mobileTheme === 'white' ? <Brightness4 /> : <Brightness7 />}
            </IconButton>
          )}
        </Box>

        <TechBackground isLightTheme={mobileTheme === 'white'} />

        <Typography
          variant="h4"
          sx={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            maxWidth: 520,
            px: 2,
            mb: 1,
            color: mobileTheme === 'white' ? '#333' : '#fff',
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' },
            textAlign: 'center',
          }}
        >
          {pl.title}
        </Typography>
        
        {checkingProject ? (
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography 
              variant="body2" 
              sx={{ 
                color: mobileTheme === 'white' ? '#666' : '#aaa',
              }}
            >
              {pl.checkingProject}
            </Typography>
          </Box>
        ) : projectInfo && (
          <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', mb: 2 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: projectExists 
                  ? (mobileTheme === 'white' ? '#2E7D32' : '#4CAF50')
                  : 'error.main',
                fontWeight: 'bold',
                mb: projectExists ? 0 : 1,
              }}
            >
              {projectInfo.name}
            </Typography>
            {!projectExists && (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'error.main',
                  mt: 0.5,
                  fontStyle: 'italic',
                }}
              >
                {pl.projectMissingHint}
              </Typography>
            )}
          </Box>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: {
              xs: 'calc(100% - 32px)',
              sm: 'calc(100% - 64px)',
              md: '100%'
            },
            maxWidth: {
              xs: 320,
              sm: 380,
              md: 400
            },
            bgcolor: mobileTheme === 'white' ? '#fff' : '#1a2332',
            p: {
              xs: 2,
              sm: 2.5,
              md: 3
            },
            mx: {
              xs: 2,
              sm: 3,
              md: 2
            },
            my: {
              xs: 1,
              sm: 2,
            },
            borderRadius: 2,
            boxShadow: mobileTheme === 'white' 
              ? '0 8px 32px rgba(0, 0, 0, 0.1)' 
              : '0 8px 32px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(8px)',
            border: mobileTheme === 'white' 
              ? '1px solid rgba(0, 0, 0, 0.1)' 
              : '1px solid rgba(255, 255, 255, 0.1)',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            label={pl.username}
            name="username"
            variant="outlined"
            fullWidth
            required
            autoComplete="username"
            autoFocus
            disabled={!projectExists || checkingProject}
            sx={{ 
              mb: { xs: 1.5, sm: 2 },
              '& .MuiOutlinedInput-root': {
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }
            }}
            value={formData.username}
            onChange={handleInputChange}
            inputProps={{ 
              style: { 
                color: mobileTheme === 'white' ? '#333' : '#ffffff' 
              } 
            }}
            InputLabelProps={{ 
              style: { 
                color: mobileTheme === 'white' ? '#666' : '#b0bec5' 
              } 
            }}
          />
          
          <TextField
            label={pl.password}
            name="password"
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            fullWidth
            required
            autoComplete="current-password"
            disabled={!projectExists || checkingProject}
            sx={{ 
              mb: { xs: 1.5, sm: 2 },
              '& .MuiOutlinedInput-root': {
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }
            }}
            value={formData.password}
            onChange={handleInputChange}
            inputProps={{ 
              style: { 
                color: mobileTheme === 'white' ? '#333' : '#ffffff' 
              } 
            }}
            InputLabelProps={{ 
              style: { 
                color: mobileTheme === 'white' ? '#666' : '#b0bec5' 
              } 
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => setShowPassword((prev) => !prev)}
                    size="small"
                    sx={{ color: mobileTheme === 'white' ? '#1976d2' : '#90caf9' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || !formData.username || !formData.password || !projectExists || checkingProject}
            sx={{
              mt: { xs: 1, sm: 1.5 },
              mb: 2,
              py: { xs: 1.2, sm: 1.5 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              bgcolor: mobileTheme === 'white' ? '#1976d2' : '#2196f3',
              color: '#fff',
              border: mobileTheme === 'white' ? '1px solid #1565c0' : '1px solid #1976d2',
              textShadow: mobileTheme === 'white' ? '0 1px 1px rgba(0,0,0,0.2)' : 'none',
              boxShadow: mobileTheme === 'white'
                ? '0 6px 16px rgba(25, 118, 210, 0.3)'
                : '0 6px 16px rgba(33, 150, 243, 0.35)',
              '&:hover': {
                bgcolor: mobileTheme === 'white' ? '#1565c0' : '#1976d2',
                boxShadow: mobileTheme === 'white'
                  ? '0 8px 20px rgba(25, 118, 210, 0.4)'
                  : '0 8px 20px rgba(33, 150, 243, 0.45)',
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : pl.signIn}
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <input
              id="rememberMe"
              type="checkbox"
              checked={formData.rememberMe}
              onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
              style={{ marginRight: 8 }}
            />
            <label htmlFor="rememberMe" style={{ color: mobileTheme === 'white' ? '#666' : '#b0bec5', fontSize: '0.9rem' }}>
              {pl.rememberMe}
            </label>
          </Box>

          <Button
            type="button"
            variant="text"
            size="small"
            onClick={() => navigate('/v2/login')}
            sx={{
              mt: 1,
              color: mobileTheme === 'white' ? '#1565c0' : '#90caf9',
              textTransform: 'none',
              alignSelf: 'center',
            }}
          >
            {pl.openV2Login}
          </Button>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default ProjectLoginPage;
